import { z } from 'zod';

/**
 * Zod schemas at the HTTP trust boundary for the gen-3 cohort detail page.
 * Each TanStack `queryFn` parses its raw JSON response through one of
 * these schemas before returning to React.
 *
 * Philosophy: validate the shape we depend on without crashing on the
 * runtime variations the platform produces today (null vs missing,
 * empty strings, new statuses we haven't enumerated yet, etc.). We
 * use `passthrough()` so the server can add fields, and we keep most
 * fields tolerant — string fields default to '', number fields to 0,
 * `latestStatus` is validated as a string rather than an enum so a
 * new server-side status doesn't blank the page. Required fields are
 * limited to the IDs we genuinely need to render anything at all.
 *
 * Per `libs/frontend/CLAUDE.md`: Zod at trust boundaries with types
 * derived via `z.infer`.
 */

const tolerantString = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

const tolerantNumber = z
  .number()
  .nullish()
  .transform((v) => v ?? 0);

const userTransactionSchema = z
  .object({
    userId: tolerantString,
    firstName: tolerantString,
    lastName: tolerantString,
  })
  .passthrough();

export const cohortSchema = z
  .object({
    cohortId: z.string(),
    cohortProfileId: z.string(),
    cohortName: tolerantString,
    cohortDescription: tolerantString,
    latestCohortVersionId: tolerantString,
    // Accept any string status so a new server-side enum value (e.g. a
    // new processing state) doesn't fail the parse.
    latestStatus: tolerantString,
    latestSubjectCount: tolerantNumber,
    latestDatasetReleaseId: tolerantString.optional(),
    latestDatasetReleaseDisplayName: tolerantString.optional(),
    projectId: tolerantString,
    createdBy: userTransactionSchema.optional(),
  })
  .passthrough();

export const cohortVersionSchema = z
  .object({
    cohortVersionId: z.string(),
    cohortProfileId: tolerantString,
    dslVersion: tolerantString,
    sqlValue: tolerantString,
    // jsonValue is the full legacy DSL — we accept anything here and let
    // downstream consumers narrow with their own schemas.
    jsonValue: z.unknown().optional(),
  })
  .passthrough();

export const conceptGroupSchema = z
  .object({
    conceptGroupId: z.string(),
    conceptGroupName: tolerantString,
  })
  .passthrough();

/**
 * Standard `ObjectResponse<T>` envelope from the platform gateway.
 */
export const objectResponse = <T extends z.ZodTypeAny>(inner: T) =>
  z.object({ data: inner }).passthrough();

/**
 * Standard `ListResponse<T>` envelope from the platform gateway.
 */
export const listResponse = <T extends z.ZodTypeAny>(inner: T) =>
  z.object({ data: z.array(inner) }).passthrough();

export type CohortFromApi = z.infer<typeof cohortSchema>;
export type CohortVersionFromApi = z.infer<typeof cohortVersionSchema>;
export type ConceptGroupFromApi = z.infer<typeof conceptGroupSchema>;
